import React, { useContext, useMemo } from 'react';
import { AutoLockProvider, IAutoLockProvider } from '@pwc/auto-save-ui';
import { LockHttpClient } from 'common/services/httpClient';
import featureFlags, {
  isInlineCommentingEnabled,
} from 'common/helpers/featureFlags';

export type AutoLockProps = {
  diableAutoLockOverride?: boolean;
} & IAutoLockProvider;

// TODO use useAutoLockCommentingContext to acccess deeply nested contentThreadIds
export type AutoLockContextValue = {
  contentThreadIds: number[];
  projectId: number | null;
};

const AutoLockContext = React.createContext<AutoLockContextValue>({
  contentThreadIds: [],
  projectId: null,
});

export const useAutoLockCommentingContext = () => {
  return useContext(AutoLockContext);
};

interface AutoLockProviderProps {
  children: React.ReactNode;
  contentThreadIds: number[];
  projectId: number;
}

export const AutoLockInlineProvider = ({
  children,
  contentThreadIds,
  projectId,
}: AutoLockProviderProps) => {
  const memoizedValue = useMemo(() => {
    return {
      contentThreadIds,
      projectId,
    };
  }, [contentThreadIds, projectId]);

  return (
    <AutoLockContext.Provider value={memoizedValue}>
      {children}
    </AutoLockContext.Provider>
  );
};

export const AutoLock = ({
  diableAutoLockOverride = false,
  projectId,
  children,
  contentThreadIds,
  ...rest
}: AutoLockProps) => {
  const enableAutoLock =
    (featureFlags.isAutoSaveEnabled && !diableAutoLockOverride) ||
    (isInlineCommentingEnabled && !diableAutoLockOverride);
  const enableReactQueryDevTools =
    process.env.REACT_APP_AUTO_SAVE_ISDEV === 'true';

  return (
    <AutoLockProvider
      projectId={Number(projectId)}
      contentThreadIds={contentThreadIds}
      {...rest}
      config={{
        enabled: Boolean(enableAutoLock),
        httpClient: LockHttpClient(projectId),
        autoLockTimerIntervalDelay: Number(
          process.env.REACT_APP_AUTO_LOCK_DELAY
        ),
      }}
      isDev={enableReactQueryDevTools}
    >
      <AutoLockInlineProvider
        contentThreadIds={contentThreadIds}
        projectId={Number(projectId)}
      >
        {children}
      </AutoLockInlineProvider>
    </AutoLockProvider>
  );
};

export default AutoLock;
